import { useEffect, useRef, useState } from "react";

import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";

import { PaymentsPayinService, PayinRuntimeStatus } from "../services/paymentsPayin.service";
import { nextPayinPollDelay, PAYIN_POLL_FAST_MS, payinPollDeadline } from "../utils/payinLivePolling";
import { advancePayinStatus } from "../utils/payinStatusProgress";

const TERMINAL_STATUSES: PayinRuntimeStatus[] = [
  PayinRuntimeStatus.CONFIRMED,
  PayinRuntimeStatus.REJECTED,
  PayinRuntimeStatus.FAILED,
  PayinRuntimeStatus.REVERSED,
];

export function isTerminalPayinStatus(status: PayinRuntimeStatus | null): boolean {
  return status != null && TERMINAL_STATUSES.includes(status);
}

/**
 * Live status of a QR payin while its screen is open, so the UI flips to "paid" the moment the provider
 * webhook settles it. Two channels that coexist (the state update is idempotent):
 *
 * - **Socket** — `payin:updated` on the notifications singleton (rule 10: never open a second socket), filtered
 *   by payin id. Instant, but advisory: a dropped connection loses the event.
 * - **Polling fallback** — `GET /payins/:id` while the status is non-terminal: every 5 s at first, then every
 *   15 s, until the instrument's own expiry plus a grace (`payinPollDeadline`; 60 minutes, a Bre-B QR's life,
 *   when the caller has no expiry). Stopping earlier left a QR paid in its last minutes showing as pending,
 *   still offered, whenever the socket had dropped. A hidden tab spends no reads, and coming back to it reads
 *   at once, also past the deadline. Poll errors are swallowed: this hook only ever upgrades the UI.
 *
 * Both channels go through `advancePayinStatus`, because they answer out of order: a `GET` sent before the
 * webhook can resolve after the socket said CONFIRMED, and taking it would put a paid payin back on its "how
 * to pay" screen. A reading only wins when the backend could have moved the payin there.
 *
 * Pass `null` to disable (e.g. before the payin exists).
 */
export function usePayinLiveStatus(
  payinId: string | null,
  options: { expiresAt?: string | null } = {},
): PayinRuntimeStatus | null {
  const [status, setStatus] = useState<PayinRuntimeStatus | null>(null);
  // What the poll checks before each read; it follows the committed status.
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  const expiresAt = options.expiresAt ?? null;

  // A new payin id is a new lifecycle — drop the previous one's status. Adjusted during render (the
  // React-documented pattern, as in `usePayoutLiveStatus`) so the new payin never renders through the old
  // one's status for a frame.
  const [seenId, setSeenId] = useState(payinId);
  if (payinId !== seenId) {
    setSeenId(payinId);
    setStatus(null);
  }

  // Socket channel.
  useEffect(() => {
    if (!payinId) return;
    return notificationsSocketService.onPayinUpdate((payload) => {
      if (payload.id === payinId) {
        setStatus((current) => advancePayinStatus(current, payload.status));
      }
    });
  }, [payinId]);

  // Polling fallback.
  useEffect(() => {
    if (!payinId) return;
    // A read still in flight when the payin changes (or the screen closes) belongs to the previous one.
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    const deadline = payinPollDeadline(startedAt, expiresAt);
    const isSettled = () => isTerminalPayinStatus(statusRef.current);

    const read = () => {
      PaymentsPayinService.getPayin(payinId)
        .then((res) => {
          if (active) setStatus((current) => advancePayinStatus(current, res.data.status));
        })
        .catch(() => {
          // Transient read failures must not kill the screen — the next tick retries.
        });
    };

    const tick = () => {
      timer = null;
      if (!active || isSettled()) return;
      if (!document.hidden) read();
      const delay = nextPayinPollDelay(startedAt, Date.now(), deadline);
      if (delay !== null) timer = setTimeout(tick, delay);
    };

    const onVisible = () => {
      if (!document.hidden && !isSettled()) read();
    };

    timer = setTimeout(tick, PAYIN_POLL_FAST_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [payinId, expiresAt]);

  return status;
}
